import React, { useState, useRef, useEffect } from "react";
import { t } from "i18next";

import { AppColumn, AppRow } from "../../../UI/Layouts";
import { UserAvatar } from "../../../UI/DisplayImage";
import {
  MultiUserAvatarGrid,
  getMultiUserDisplayName,
} from "../../../UI/MultiUserAvatarGrid";
import { SizedBox } from "../../../UI/Sizes";
import AppText from "../../../UI/Texts/AppTexts";
import {
  getTimeStamp,
  getUserName,
  getLocale,
  getTimeAgo,
} from "../../../../utils/helperFunctions";
import useTheme from "../../../../hooks/useTheme";
import { useResponsive } from "../../../UI/ResponsiveContext";
import { Icon, Dropdown } from "semantic-ui-react";
import { useSelector } from "react-redux";
import { MODAL_CANDIDATE_DETAIL } from "../../../Candidate/CandidateDetail/CandidateViewPage";

export const ChatMessageHeader = ({
  currentChatData,
  currentUser,
  contactWorkplaceName,
  onClick,
  closeModal,
  openModal,
}) => {
  const { isMobile } = useResponsive();
  const theme = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const { appliedJobs } = useSelector((state) => state.auth);
  const coreConstants = useSelector((state) => state.core.constants) ?? {};

  const currentUserId = currentUser?.uid;

  const { profilesData, lastMessage, metaData } = currentChatData;

  const isRelationshipChat = metaData?.workplaceId != null;

  const matchingApplications = React.useMemo(() => {
    if (!isRelationshipChat || !appliedJobs) return [];

    const filtered = appliedJobs.filter((job) => {
      return job.tenantId === metaData?.workplaceId;
    });

    // Sort by date (newest first)
    return filtered.sort((a, b) => {
      const dateA = a.appliedAt || a.createdAt || 0;
      const dateB = b.appliedAt || b.createdAt || 0;
      return dateB - dateA;
    });
  }, [appliedJobs, metaData, profilesData, isRelationshipChat]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSeeApplication = () => {
    if (matchingApplications.length === 1) {
      const app = matchingApplications[0];
      openModal(MODAL_CANDIDATE_DETAIL, {
        candidateData: app,
        jobId: app.jobDetails?.jobId ?? app.jobId,
      });
    } else if (matchingApplications.length > 1) {
      setShowDropdown(!showDropdown);
    }
  };

  const handleSelectApplication = (app) => {
    setShowDropdown(false);
    openModal(MODAL_CANDIDATE_DETAIL, {
      candidateData: app,
      jobId: app.jobDetails?.jobId ?? app.jobId,
    });
  };

  const getJobTitle = (app) => {
    const profession = app.jobDetails?.profession ?? app.profession;
    const roles = coreConstants.workroles ?? [];
    const roleText =
      roles.find((r) => r.id === profession)?.text?.[getLocale()] ?? profession;
    return roleText || "Ansökan";
  };

  const getHealthcareWorkplaceName = (app) => {
    return app.jobDetails?.healthcareWorkplace?.name || "";
  };

  // Portal is candidate-side: prefer recruiter profiles, fall back to any
  // non-self profile if no recruiter is present. Skip inactive or
  // "Deleted User" sentinel profiles so they don't surface in the header.
  const isVisibleProfile = (profile) =>
    profile?.active !== false && profile?.name !== "Deleted User";

  const visibleOtherProfiles = Object.values(profilesData || {}).filter(
    (e) => currentUserId !== e.userId && isVisibleProfile(e),
  );
  const recruiterProfiles = visibleOtherProfiles.filter(
    (e) => e.role === "recruiter",
  );
  const otherUserProfiles = (
    recruiterProfiles.length > 0 ? recruiterProfiles : visibleOtherProfiles
  ).map((profile) => ({
    userId: profile.userId,
    name: profile.name || getUserName(profile),
    profileImageUrl: profile.profileImageUrl,
    lastActiveAt: profile.lastActiveAt,
  }));

  const isOnline = (profile) => {
    if (!profile?.lastActiveAt) return false;
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    return profile.lastActiveAt > fiveMinutesAgo;
  };

  // Check if any user is online
  const anyUserIsOnline = otherUserProfiles.some((profile) =>
    isOnline(profile),
  );

  const displayName = getMultiUserDisplayName(otherUserProfiles, t);

  const profileImageSize = isMobile ? 50 : 50;

  // Get most recent activity from any user
  const lastMessageSentAt = otherUserProfiles.reduce((latest, profile) => {
    const profileTime = profile.lastActiveAt || 0;
    return profileTime > latest ? profileTime : latest;
  }, lastMessage?.createdAt || 0);

  return (
    <AppColumn
      mainAxisAlignment={"center"}
      style={{
        paddingTop: theme.spacing.elementPadding,
        paddingBottom: theme.spacing.elementPadding,
        paddingLeft: theme.spacing.contentPadding,
        paddingRight: isMobile && theme.spacing.contentPadding,
        cursor: onClick ? "pointer" : "default",
        width: isMobile && "100%",
      }}
    >
      <AppRow
        mainAxisAlignment={"space-between"}
        crossAxisAlignment={isMobile ? "start" : "center"}
      >
        {isMobile && (
          <Icon
            name="close"
            size="large"
            style={{
              cursor: "pointer",
              marginRight: theme.spacing.elementPadding,
              marginTop: isMobile && "0.5rem",
            }}
            onClick={() => {
              closeModal();
            }}
          />
        )}

        <AppRow
          onClick={onClick}
          crossAxisAlignment={"start"}
          style={{
            flex: 1,
            width: "100%",
          }}
        >
          {" "}
          <MultiUserAvatarGrid
            profiles={otherUserProfiles}
            size={profileImageSize}
          />
          <SizedBox width={theme.spacing.elementPadding} />
          <AppColumn
            crossAxisAlignment={"start"}
            style={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
          >
            <AppText.Caption fontWeight={600}>{displayName}</AppText.Caption>
            {contactWorkplaceName && (
              <>
                <SizedBox height={"0.1rem"} />
                <AppText.Footnote color={theme.colors.grey}>
                  {contactWorkplaceName}
                </AppText.Footnote>
              </>
            )}
            <SizedBox height={"0.15rem"} />
            {anyUserIsOnline ? (
              <AppText.Footnote>{t("chat.active")}</AppText.Footnote>
            ) : (
              <AppText.Footnote color={theme.colors.grey}>
                {getTimeStamp(lastMessageSentAt)}
              </AppText.Footnote>
            )}
          </AppColumn>
        </AppRow>
        {isRelationshipChat && matchingApplications.length > 0 && (
          <div ref={dropdownRef} style={{ position: "relative" }}>
            <AppRow
              onClick={handleSeeApplication}
              style={{
                cursor: "pointer",
                marginLeft: theme.spacing.elementPadding,
              }}
            >
              <AppText.Caption color={theme.primaryColor} fontWeight={600}>
                {t("chat.see_application")}
              </AppText.Caption>
              {matchingApplications.length > 1 && (
                <Icon
                  name={showDropdown ? "chevron up" : "chevron down"}
                  style={{ color: theme.primaryColor, marginLeft: "4px" }}
                />
              )}
            </AppRow>
            {showDropdown && matchingApplications.length > 1 && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  backgroundColor: theme.colors.white,
                  border: `1px solid ${theme.colors.lightGrey}`,
                  borderRadius: theme.radius.small,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 1000,
                  minWidth: "300px",
                  marginTop: "4px",
                }}
              >
                {matchingApplications.map((app, index) => (
                  <AppColumn
                    key={app.jobId || index}
                    onClick={() => handleSelectApplication(app)}
                    style={{
                      padding: theme.spacing.elementPadding,
                      cursor: "pointer",
                      borderBottom:
                        index < matchingApplications.length - 1
                          ? `1px solid ${theme.colors.lightGrey}`
                          : "none",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = theme.colors.lightGrey)
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "transparent")
                    }
                  >
                    <AppText.BodyText>{getJobTitle(app)}</AppText.BodyText>
                    <AppText.BodyText color={theme.colors.grey}>
                      {getHealthcareWorkplaceName(app)}
                    </AppText.BodyText>
                    <AppText.Footnote color={theme.colors.grey}>
                      {getTimeAgo(app.appliedAt || app.createdAt, true)}
                    </AppText.Footnote>
                  </AppColumn>
                ))}
              </div>
            )}
          </div>
        )}
      </AppRow>
    </AppColumn>
  );
};
